import * as React from "react";
import { Rocket } from "lucide-react";
import { Button, Dialog, useToast } from "@chotca/ui";
import {
  useBoostPosting,
  usePlanStatus,
  usePostingBoostPreview,
  usePostingBoostSummary,
} from "@chotca/api-client";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

export function BoostPostingButton({
  postingId,
  orgId,
  isBoosted,
  boostedUntil,
}: {
  postingId: string;
  orgId: string;
  isBoosted: boolean;
  boostedUntil: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();
  const { openUpgrade, openTopup } = useSubscriptionModals();
  const { data: plan } = usePlanStatus(orgId);
  const preview = usePostingBoostPreview(postingId, open);
  const boost = useBoostPosting(postingId, orgId);

  if (isBoosted && boostedUntil) {
    return (
      <span className="inline-flex items-center rounded-lg bg-ember/10 px-3 py-1.5 text-xs font-bold text-ember">
        <Rocket size={14} strokeWidth={2.25} className="mr-1.5" />
        Đang đẩy đến {formatDateTime(boostedUntil)}
      </span>
    );
  }

  const isFree = (plan?.effectiveCode ?? "FREE") === "FREE";
  const remaining = plan?.boostRemaining ?? 0;

  function onClick() {
    if (isFree) openUpgrade();
    else if (remaining <= 0) openTopup("BOOST");
    else setOpen(true);
  }

  async function onConfirm() {
    try {
      await boost.mutateAsync();
      toast({ tone: "success", title: "Đã đẩy tin" });
      setOpen(false);
    } catch (e) {
      toast({
        tone: "error",
        title:
          e instanceof Error ? e.message : "Chưa đẩy được tin, thử lại sau",
      });
    }
  }

  const estimated = preview.data?.estimatedRecipients ?? 0;
  const noAudience = preview.isSuccess && estimated === 0;

  return (
    <>
      <Button size="sm" onClick={onClick}>
        <Rocket size={14} strokeWidth={2.25} className="mr-1.5" />
        Đẩy tin
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        title="Đẩy tin này?"
        description={`Dùng 1 lượt, còn ${remaining} lượt trong kỳ.`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Để sau
            </Button>
            <Button
              size="sm"
              onClick={onConfirm}
              loading={boost.isPending}
              disabled={!preview.isSuccess || noAudience}
            >
              Đẩy tin
            </Button>
          </div>
        }
      >
        {preview.isPending ? (
          <div className="h-16 animate-pulse rounded-lg bg-ink/5" />
        ) : preview.isError ? (
          <p className="text-sm text-ink/70">
            Chưa tải được số ứng viên, thử lại sau.
          </p>
        ) : noAudience ? (
          <p className="text-sm text-ink/70">
            Chưa có ứng viên phù hợp trong bán kính {preview.data.radiusKm} km,
            hoặc họ vừa được báo về tin này. Lượt của bạn chưa bị trừ, hãy thử
            lại sau.
          </p>
        ) : (
          <ul className="space-y-2 text-sm text-ink/80">
            <li>
              Gửi thông báo tới khoảng <strong>{estimated} ứng viên</strong> phù
              hợp trong bán kính {preview.data.radiusKm} km.
            </li>
            <li>
              Tin hiện nhãn &quot;Tuyển gấp&quot; và được ưu tiên trong danh
              sách {preview.data.durationDays} ngày.
            </li>
            <li className="text-xs text-ink/60">
              Thông báo chỉ gửi trong khung 8h–21h. Số người nhận có thể thay
              đổi lúc gửi. Đẩy tin không đảm bảo số đơn ứng tuyển.
            </li>
          </ul>
        )}
      </Dialog>
    </>
  );
}

export function BoostSummaryCard({ postingId }: { postingId: string }) {
  const { data } = usePostingBoostSummary(postingId);
  const boost = data?.boost;
  if (!boost) return null;

  const recipients = boost.pushRecipients + boost.emailRecipients;
  return (
    <div className="rounded-xl border border-ink/10 bg-paper p-5">
      <h3 className="mb-1 text-sm font-semibold">Đợt đẩy tin gần nhất</h3>
      <p className="mb-3 text-xs text-ink/60">
        {formatDateTime(boost.startedAt)} – {formatDateTime(boost.endsAt)}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xl font-extrabold text-ink">
            {boost.fanoutAt ? recipients : "—"}
          </p>
          <p className="text-xs text-ink/60">
            {boost.fanoutAt
              ? "Người được gửi thông báo"
              : "Đang chờ gửi thông báo"}
          </p>
        </div>
        <div>
          <p className="text-xl font-extrabold text-ink">
            {boost.applications}
          </p>
          <p className="text-xs text-ink/60">Đơn nhận trong đợt</p>
        </div>
      </div>
    </div>
  );
}
